import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import ButtonWithSpinner from "../ButtonWithSpinner";

const useStyles = makeStyles(theme => ({
  contactPreview: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginTop: 18,
    padding: 14,
    borderRadius: 12,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(59, 130, 246, 0.12)"
        : "rgba(37, 99, 235, 0.07)",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(96, 165, 250, 0.25)"
        : "1px solid rgba(37, 99, 235, 0.16)",
  },
  contactDetails: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
  contactName: {
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

const ContactCardModal = ({ open, onClose, ticketId }) => {
  const classes = useStyles();
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchParam, setSearchParam] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    let active = true;
    setLoading(true);
    const debounce = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts", {
          params: { searchParam },
        });
        if (active) {
          setContacts(
            data.contacts.filter(contact => !contact.isGroup && contact.number)
          );
        }
      } catch (err) {
        if (active) toastError(err);
      } finally {
        if (active) setLoading(false);
      }
    }, 350);

    return () => {
      active = false;
      clearTimeout(debounce);
    };
  }, [open, searchParam]);

  const handleClose = () => {
    if (sending) return;
    setSelectedContact(null);
    setSearchParam("");
    setContacts([]);
    onClose();
  };

  const handleSend = async () => {
    if (!selectedContact?.id) return;

    setSending(true);
    try {
      await api.post(`/messages/${ticketId}/contact-card`, {
        contactId: selectedContact.id,
      });
      toast.success(i18n.t("messagesInput.contactCardSent"));
      setSelectedContact(null);
      setSearchParam("");
      setContacts([]);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{i18n.t("messagesInput.contactCardTitle")}</DialogTitle>
      <DialogContent dividers>
        <Autocomplete
          options={contacts}
          value={selectedContact}
          loading={loading}
          autoHighlight
          filterOptions={options => options}
          getOptionLabel={option =>
            option?.name
              ? `${option.name} - ${option.number}`
              : option?.number || ""
          }
          onChange={(event, value) => setSelectedContact(value)}
          onInputChange={(event, value, reason) => {
            if (reason === "input" || reason === "clear") {
              setSearchParam(value);
            }
          }}
          renderInput={params => (
            <TextField
              {...params}
              autoFocus
              variant="outlined"
              label={i18n.t("messagesInput.contactCardSearch")}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {selectedContact && (
          <div className={classes.contactPreview}>
            <Avatar src={selectedContact.profilePicUrl}>
              {selectedContact.name?.charAt(0)}
            </Avatar>
            <div className={classes.contactDetails}>
              <Typography className={classes.contactName}>
                {selectedContact.name || selectedContact.number}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                +{selectedContact.number}
              </Typography>
              {selectedContact.email ? (
                <Typography variant="body2" color="textSecondary">
                  {selectedContact.email}
                </Typography>
              ) : null}
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="secondary"
          variant="outlined"
          disabled={sending}
        >
          {i18n.t("messagesInput.contactCardCancel")}
        </Button>
        <ButtonWithSpinner
          onClick={handleSend}
          color="primary"
          variant="contained"
          disabled={!selectedContact || sending}
          loading={sending}
        >
          {i18n.t("messagesInput.contactCardSend")}
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export default ContactCardModal;
